import React from 'react'
import { Spin } from 'antd'
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom'
import RouteGuard from '@/components/RouteGuard/RouteGuard'
import PermissionGuard from '@/components/RouteGuard/PermissionGuard'
import { lazyWithRetry } from '@/utils/lazyWithRetry'

const LoginPage = lazyWithRetry(() => import('@/pages/Login'))
const AppLayout = lazyWithRetry(() => import('@/components/Layout/AppLayout'))
const Dashboard = lazyWithRetry(() => import('@/pages/Dashboard'))
const MenuManagementPage = lazyWithRetry(() => import('@/pages/System/Menu'))
const RoleManagementPage = lazyWithRetry(() => import('@/pages/System/Role'))
const AdminManagementPage = lazyWithRetry(() => import('@/pages/System/Admin'))
const TradingPairListPage = lazyWithRetry(() => import('@/pages/Trading/TradingPairList'))
const KycListPage = lazyWithRetry(() => import('@/pages/User/KycList'))
const AdvancedVerificationListPage = lazyWithRetry(
  () => import('@/pages/User/AdvancedVerificationList')
)
const SpousalLinkApplicationsPage = lazyWithRetry(
  () => import('@/pages/User/SpousalLinkApplications')
)
const UserListPage = lazyWithRetry(() => import('@/pages/User/UserList'))
const UserDetailPage = lazyWithRetry(() => import('@/pages/User/UserDetail'))
const RegistrationCodePage = lazyWithRetry(() => import('@/pages/User/RegistrationCode'))
const AccountOverviewLookupPage = lazyWithRetry(() => import('@/pages/User/AccountOverview'))
const AnnouncementPage = lazyWithRetry(() => import('@/pages/Config/Announcement'))
const ActivityPopupsPage = lazyWithRetry(() => import('@/pages/Config/ActivityPopups'))
const WalletAccountsPage = lazyWithRetry(() => import('@/pages/Wallet/Accounts'))
const WalletLedgerPage = lazyWithRetry(() => import('@/pages/Wallet/Ledger'))
const WalletDepositsPage = lazyWithRetry(() => import('@/pages/Wallet/Deposits'))
const WalletWithdrawalsPage = lazyWithRetry(() => import('@/pages/Wallet/Withdrawals'))
const WalletDepositAddressesPage = lazyWithRetry(() => import('@/pages/Wallet/DepositAddresses'))
const WalletPlatformDepositAddressesPage = lazyWithRetry(
  () => import('@/pages/Wallet/PlatformDepositAddresses')
)
const WalletConvertOrdersPage = lazyWithRetry(() => import('@/pages/Wallet/ConvertOrders'))
const WalletConvertExposurePage = lazyWithRetry(() => import('@/pages/Wallet/ConvertExposure'))
const EarnProductsPage = lazyWithRetry(() => import('@/pages/Earn/Products'))
const EarnHoldingsPage = lazyWithRetry(() => import('@/pages/Earn/Holdings'))
const EarnOrdersPage = lazyWithRetry(() => import('@/pages/Earn/Orders'))
const EarnRedemptionsPage = lazyWithRetry(() => import('@/pages/Earn/Redemptions'))
const EarnRewardsPage = lazyWithRetry(() => import('@/pages/Earn/Rewards'))
const ArbitrageProductsPage = lazyWithRetry(() => import('@/pages/Arbitrage/Products'))
const ArbitrageHoldingsPage = lazyWithRetry(() => import('@/pages/Arbitrage/Holdings'))
const ArbitrageOrdersPage = lazyWithRetry(() => import('@/pages/Arbitrage/Orders'))
const ArbitrageRedemptionsPage = lazyWithRetry(() => import('@/pages/Arbitrage/Redemptions'))
const ArbitrageRewardsPage = lazyWithRetry(() => import('@/pages/Arbitrage/Rewards'))
const MiningProductsPage = lazyWithRetry(() => import('@/pages/Mining/Products'))
const MiningOrdersPage = lazyWithRetry(() => import('@/pages/Mining/Orders'))
const MiningPayoutsPage = lazyWithRetry(() => import('@/pages/Mining/Payouts'))
// 采矿模块三页尚未实现（拆自 #1182，1/4），暂指向占位页，由后续三个页面 issue 各自替换
const TradingContractOrdersPage = lazyWithRetry(() => import('@/pages/Trading/ContractOrders'))
const TradingSpotOrdersPage = lazyWithRetry(() => import('@/pages/Trading/SpotOrders'))
const ContractPositionsPage = lazyWithRetry(() => import('@/pages/Trading/ContractPositions'))
const ContractRiskSnapshotPage = lazyWithRetry(() => import('@/pages/Trading/ContractRiskSnapshot'))
const ContractLiquidationsPage = lazyWithRetry(() => import('@/pages/Trading/ContractLiquidations'))
const ContractFundingHistoryPage = lazyWithRetry(
  () => import('@/pages/Trading/ContractFundingHistory')
)
const BinaryOptionsConfigPage = lazyWithRetry(() => import('@/pages/Trading/BinaryOptionsConfig'))
const BinaryOptionsOrdersPage = lazyWithRetry(() => import('@/pages/Trading/BinaryOptionsOrders'))
const BinaryOptionsUserOverridesPage = lazyWithRetry(
  () => import('@/pages/Trading/BinaryOptionsUserOverrides')
)
const SystemSettingsPage = lazyWithRetry(() => import('@/pages/System/Settings'))
const ModuleCapabilitiesPage = lazyWithRetry(() => import('@/pages/System/Modules'))
const SystemLogsPage = lazyWithRetry(() => import('@/pages/System/Logs'))
const SystemMessagesPage = lazyWithRetry(() => import('@/pages/System/Messages'))
const NewsSourcesPage = lazyWithRetry(() => import('@/pages/News/NewsSources'))
const NewsArticlesPage = lazyWithRetry(() => import('@/pages/News/NewsArticles'))
const NewsCategoriesPage = lazyWithRetry(() => import('@/pages/News/NewsCategories'))
const MarketAnalysesPage = lazyWithRetry(() => import('@/pages/News/MarketAnalyses'))
const AiStrategiesPage = lazyWithRetry(() => import('@/pages/AiQuant/Strategies'))
const AiTemplatesPage = lazyWithRetry(() => import('@/pages/AiQuant/Templates'))
const AiTradesPage = lazyWithRetry(() => import('@/pages/AiQuant/Trades'))
const FileListPage = lazyWithRetry(() => import('@/pages/File/FileList'))
const MarginConfigsPage = lazyWithRetry(() => import('@/pages/Margin/Configs'))
const MarginLoansPage = lazyWithRetry(() => import('@/pages/Margin/Loans'))
const MarginContractTemplatesPage = lazyWithRetry(() => import('@/pages/Margin/ContractTemplates'))
const MarginRepaymentClaimsPage = lazyWithRetry(() => import('@/pages/Margin/RepaymentClaims'))
const MarginRepaymentAddressesPage = lazyWithRetry(
  () => import('@/pages/Margin/RepaymentAddresses')
)
const OtcOrdersPage = lazyWithRetry(() => import('@/pages/OTC/Orders'))
const OtcPaymentAccountsPage = lazyWithRetry(() => import('@/pages/OTC/PaymentAccounts'))
const OtcQuotesPage = lazyWithRetry(() => import('@/pages/OTC/Quotes'))
const QuantFundProductsPage = lazyWithRetry(() => import('@/pages/QuantFund/Products'))
const PromotionCampaignsPage = lazyWithRetry(() => import('@/pages/Promotion/Campaigns'))
const PromotionAssignmentsPage = lazyWithRetry(() => import('@/pages/Promotion/Assignments'))
const ReferralRelationsPage = lazyWithRetry(() => import('@/pages/Referral/Relations'))
const QuantFundOrdersPage = lazyWithRetry(() => import('@/pages/QuantFund/Orders'))
const QuantFundUserProductsPage = lazyWithRetry(() => import('@/pages/QuantFund/UserProducts'))
const QuantFundParticipationLimitsPage = lazyWithRetry(
  () => import('@/pages/QuantFund/ParticipationLimits')
)
const CopyTradeTradersPage = lazyWithRetry(() => import('@/pages/CopyTrade/Traders'))
const CopyTradePositionsPage = lazyWithRetry(() => import('@/pages/CopyTrade/Positions'))
const CopyTradeSubscriptionsPage = lazyWithRetry(() => import('@/pages/CopyTrade/Subscriptions'))
const CopyTradeExecutionLogsPage = lazyWithRetry(() => import('@/pages/CopyTrade/ExecutionLogs'))
const CustomerServiceWorkbenchPage = lazyWithRetry(
  () => import('@/pages/CustomerService/Workbench')
)
const CustomerServiceCannedMessagesPage = lazyWithRetry(
  () => import('@/pages/CustomerService/CannedMessages')
)
const SupportTicketListPage = lazyWithRetry(() => import('@/pages/Support/TicketList'))
const SupportMailboxListPage = lazyWithRetry(() => import('@/pages/Support/MailboxList'))
const SupportSenderDenylistPage = lazyWithRetry(() => import('@/pages/Support/SenderDenylist'))
const SupportDenylistHitsPage = lazyWithRetry(() => import('@/pages/Support/DenylistHits'))
const SupportPostmarkEventsPage = lazyWithRetry(() => import('@/pages/Support/PostmarkEvents'))
const KbBotChatsPage = lazyWithRetry(() => import('@/pages/Kb/BotChats'))
const KbBotConversationsPage = lazyWithRetry(() => import('@/pages/Kb/BotConversations'))
const ContentPagesPage = lazyWithRetry(() => import('@/pages/Content/ContentPages'))
const HomeLayoutsPage = lazyWithRetry(() => import('@/pages/Content/HomeLayouts'))
const ContentBannersPage = lazyWithRetry(() => import('@/pages/Content/Banners'))
const HomeLayoutEditorPage = lazyWithRetry(() => import('@/pages/Content/HomeLayouts/Editor'))
const SurveysPage = lazyWithRetry(() => import('@/pages/Content/Surveys'))
const RiskLoginHistoryPage = lazyWithRetry(() => import('@/pages/Risk/LoginHistory'))
const RiskDeviceAccountsPage = lazyWithRetry(() => import('@/pages/Risk/DeviceAccounts'))
const PriceMonitorTasksPage = lazyWithRetry(() => import('@/pages/PriceMonitor/Tasks'))
const CharityProjectsPage = lazyWithRetry(() => import('@/pages/Charity/Projects'))
const CharityDonationsPage = lazyWithRetry(() => import('@/pages/Charity/Donations'))
const CharityBannersPage = lazyWithRetry(() => import('@/pages/Charity/Banners'))
const CharitySettingsPage = lazyWithRetry(() => import('@/pages/Charity/Settings'))
const NotificationEventsPage = lazyWithRetry(() => import('@/pages/Notification/Events'))
const NotFoundPage = lazyWithRetry(() => import('@/pages/Error/NotFoundPage'))

/** Full-screen centered spinner used as Suspense fallback for top-level routes */
const FullPageSpin = (
  <div className="flex items-center justify-center w-full h-screen">
    <Spin size="large" />
  </div>
)

/** Content-area centered spinner used as Suspense fallback for child routes */
const ContentSpin = (
  <div className="flex items-center justify-center w-full h-full min-h-64">
    <Spin size="large" />
  </div>
)

/** Wraps a page element with PermissionGuard + Suspense */
function PageTransition({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()

  return (
    <div key={pathname} className="h-full" style={{ animation: 'page-enter 0.3s ease-out both' }}>
      {children}
    </div>
  )
}

function guarded(element: React.ReactNode) {
  return (
    <PermissionGuard>
      <React.Suspense fallback={ContentSpin}>
        <PageTransition>{element}</PageTransition>
      </React.Suspense>
    </PermissionGuard>
  )
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <React.Suspense fallback={FullPageSpin}>
        <LoginPage />
      </React.Suspense>
    ),
  },
  {
    path: '/',
    element: (
      <RouteGuard>
        <React.Suspense fallback={FullPageSpin}>
          <AppLayout />
        </React.Suspense>
      </RouteGuard>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: guarded(<Dashboard />) },
      { path: 'system/menu', element: guarded(<MenuManagementPage />) },
      { path: 'system/role', element: guarded(<RoleManagementPage />) },
      { path: 'system/admin', element: guarded(<AdminManagementPage />) },
      { path: 'trading/pairs', element: guarded(<TradingPairListPage />) },
      { path: 'user/kyc', element: guarded(<KycListPage />) },
      {
        path: 'user/advanced-verification',
        element: guarded(<AdvancedVerificationListPage />),
      },
      {
        path: 'user/spousal-link-applications',
        element: guarded(<SpousalLinkApplicationsPage />),
      },
      { path: 'user/list', element: guarded(<UserListPage />) },
      { path: 'user/registration-code', element: guarded(<RegistrationCodePage />) },
      { path: 'user/account-overview', element: guarded(<AccountOverviewLookupPage />) },
      { path: 'user/detail/:id', element: guarded(<UserDetailPage />) },
      { path: 'config/announcement', element: guarded(<AnnouncementPage />) },
      { path: 'config/activity-popups', element: guarded(<ActivityPopupsPage />) },
      { path: 'wallet/accounts', element: guarded(<WalletAccountsPage />) },
      { path: 'wallet/ledger', element: guarded(<WalletLedgerPage />) },
      { path: 'wallet/deposits', element: guarded(<WalletDepositsPage />) },
      { path: 'wallet/withdrawals', element: guarded(<WalletWithdrawalsPage />) },
      {
        path: 'wallet/deposit-addresses',
        element: guarded(<WalletDepositAddressesPage />),
      },
      {
        path: 'wallet/platform-deposit-addresses',
        element: guarded(<WalletPlatformDepositAddressesPage />),
      },
      { path: 'earn/products', element: guarded(<EarnProductsPage />) },
      { path: 'earn/holdings', element: guarded(<EarnHoldingsPage />) },
      { path: 'earn/orders', element: guarded(<EarnOrdersPage />) },
      { path: 'earn/redemptions', element: guarded(<EarnRedemptionsPage />) },
      { path: 'earn/rewards', element: guarded(<EarnRewardsPage />) },
      { path: 'arbitrage/products', element: guarded(<ArbitrageProductsPage />) },
      { path: 'arbitrage/holdings', element: guarded(<ArbitrageHoldingsPage />) },
      { path: 'arbitrage/orders', element: guarded(<ArbitrageOrdersPage />) },
      { path: 'arbitrage/redemptions', element: guarded(<ArbitrageRedemptionsPage />) },
      { path: 'arbitrage/rewards', element: guarded(<ArbitrageRewardsPage />) },
      { path: 'mining/products', element: guarded(<MiningProductsPage />) },
      { path: 'mining/orders', element: guarded(<MiningOrdersPage />) },
      { path: 'mining/payouts', element: guarded(<MiningPayoutsPage />) },
      { path: 'trading/spot-orders', element: guarded(<TradingSpotOrdersPage />) },
      { path: 'trading/contract-orders', element: guarded(<TradingContractOrdersPage />) },
      { path: 'trading/contract-positions', element: guarded(<ContractPositionsPage />) },
      {
        path: 'trading/contract-risk-snapshot',
        element: guarded(<ContractRiskSnapshotPage />),
      },
      {
        path: 'trading/contract-liquidations',
        element: guarded(<ContractLiquidationsPage />),
      },
      {
        path: 'trading/contract-funding-history',
        element: guarded(<ContractFundingHistoryPage />),
      },
      {
        path: 'trading/binary-options-config',
        element: guarded(<BinaryOptionsConfigPage />),
      },
      {
        path: 'trading/binary-options-orders',
        element: guarded(<BinaryOptionsOrdersPage />),
      },
      {
        path: 'trading/binary-options-user-overrides',
        element: guarded(<BinaryOptionsUserOverridesPage />),
      },
      { path: 'system/settings', element: guarded(<SystemSettingsPage />) },
      { path: 'system/settings/modules', element: guarded(<ModuleCapabilitiesPage />) },
      { path: 'system/logs', element: guarded(<SystemLogsPage />) },
      { path: 'system/messages', element: guarded(<SystemMessagesPage />) },
      { path: 'system/content-pages', element: guarded(<ContentPagesPage />) },
      { path: 'system/home-layouts', element: guarded(<HomeLayoutsPage />) },
      { path: 'system/home-layouts/:id', element: guarded(<HomeLayoutEditorPage />) },
      { path: 'system/banners', element: guarded(<ContentBannersPage />) },
      { path: 'system/surveys', element: guarded(<SurveysPage />) },
      { path: 'news/news-sources', element: guarded(<NewsSourcesPage />) },
      { path: 'news/news-articles', element: guarded(<NewsArticlesPage />) },
      { path: 'news/news-categories', element: guarded(<NewsCategoriesPage />) },
      { path: 'news/market-analyses', element: guarded(<MarketAnalysesPage />) },
      { path: 'ai-quant/strategies', element: guarded(<AiStrategiesPage />) },
      { path: 'ai-quant/templates', element: guarded(<AiTemplatesPage />) },
      { path: 'ai-quant/trades', element: guarded(<AiTradesPage />) },
      { path: 'file/file-list', element: guarded(<FileListPage />) },
      { path: 'margin/configs', element: guarded(<MarginConfigsPage />) },
      { path: 'margin/loans', element: guarded(<MarginLoansPage />) },
      {
        path: 'margin/contract-templates',
        element: guarded(<MarginContractTemplatesPage />),
      },
      { path: 'margin/repayment-claims', element: guarded(<MarginRepaymentClaimsPage />) },
      {
        path: 'margin/repayment-addresses',
        element: guarded(<MarginRepaymentAddressesPage />),
      },
      { path: 'otc/orders', element: guarded(<OtcOrdersPage />) },
      { path: 'otc/payment-accounts', element: guarded(<OtcPaymentAccountsPage />) },
      { path: 'otc/quotes', element: guarded(<OtcQuotesPage />) },
      { path: 'quant-funds/products', element: guarded(<QuantFundProductsPage />) },
      { path: 'quant-funds/orders', element: guarded(<QuantFundOrdersPage />) },
      { path: 'quant-funds/user-products', element: guarded(<QuantFundUserProductsPage />) },
      {
        path: 'quant-funds/participation-limits',
        element: guarded(<QuantFundParticipationLimitsPage />),
      },
      { path: 'promotion/campaigns', element: guarded(<PromotionCampaignsPage />) },
      { path: 'promotion/assignments', element: guarded(<PromotionAssignmentsPage />) },
      { path: 'referral/relations', element: guarded(<ReferralRelationsPage />) },
      // 跟单交易容器在侧边栏是 SubMenu（点击只展开不导航），deep-link 时重定向到首个子页
      { path: 'copy-trade', element: <Navigate to="/copy-trade/traders" replace /> },
      { path: 'copy-trade/traders', element: guarded(<CopyTradeTradersPage />) },
      { path: 'copy-trade/positions', element: guarded(<CopyTradePositionsPage />) },
      { path: 'copy-trade/subscriptions', element: guarded(<CopyTradeSubscriptionsPage />) },
      { path: 'copy-trade/execution-logs', element: guarded(<CopyTradeExecutionLogsPage />) },
      { path: 'wallet/convert-orders', element: guarded(<WalletConvertOrdersPage />) },
      { path: 'wallet/convert-exposure', element: guarded(<WalletConvertExposurePage />) },
      {
        path: 'customer-service/workbench',
        element: guarded(<CustomerServiceWorkbenchPage />),
      },
      {
        path: 'customer-service/canned-messages',
        element: guarded(<CustomerServiceCannedMessagesPage />),
      },
      { path: 'support/tickets', element: guarded(<SupportTicketListPage />) },
      { path: 'support/mailboxes', element: guarded(<SupportMailboxListPage />) },
      { path: 'support/sender-denylist', element: guarded(<SupportSenderDenylistPage />) },
      // 命中流水页无独立菜单，权限经 PREFIX_TO_PARENT 复用规则列表页（同 support.denylist.view）
      { path: 'support/sender-denylist/hits', element: guarded(<SupportDenylistHitsPage />) },
      // 独立权限码 support.postmark-events.view，走独立菜单节点，不经 PREFIX_TO_PARENT 复用
      { path: 'support/postmark-events', element: guarded(<SupportPostmarkEventsPage />) },
      // 答疑 bot 后台（issue #926）：path 与后端菜单声明（php-api#3696）逐字一致
      { path: 'kb/bot-chats', element: guarded(<KbBotChatsPage />) },
      { path: 'kb/bot-conversations', element: guarded(<KbBotConversationsPage />) },
      { path: 'system/notification-events', element: guarded(<NotificationEventsPage />) },
      { path: 'risk/login-history', element: guarded(<RiskLoginHistoryPage />) },
      { path: 'risk/devices/:fingerprint', element: guarded(<RiskDeviceAccountsPage />) },
      { path: 'price-monitor/tasks', element: guarded(<PriceMonitorTasksPage />) },
      { path: 'charity/projects', element: guarded(<CharityProjectsPage />) },
      { path: 'charity/donations', element: guarded(<CharityDonationsPage />) },
      { path: 'charity/banners', element: guarded(<CharityBannersPage />) },
      { path: 'charity/settings', element: guarded(<CharitySettingsPage />) },
      // Catch-all: any path inside the layout that isn't registered → 404
      {
        path: '*',
        element: (
          <React.Suspense fallback={ContentSpin}>
            <NotFoundPage />
          </React.Suspense>
        ),
      },
    ],
  },
  // Catch-all outside the layout (e.g. /some-random-top-level)
  {
    path: '*',
    element: (
      <React.Suspense fallback={FullPageSpin}>
        <NotFoundPage />
      </React.Suspense>
    ),
  },
])
